import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import useEntreprises from "../../hooks/entreprise/useEntrepries";
import Loader from "../components/loader";

import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

import { defaultImage } from "../../utils/ImageEnBase46";
import { FiSearch, FiFilter } from "react-icons/fi";

function EntreprisesList() {
  const { entreprises, setEntreprises, updateEntreprise, fetchEntreprises } =
    useEntreprises();

  const navigate = useNavigate();

  const [message, setMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statutFilter, setStatutFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const entreprisesPerPage = 10;

  // ================= FETCH ENTREPRISES =================

  useEffect(() => {
    const loadEntreprises = async () => {
      try {
        setLoading(true);
        await fetchEntreprises();
      } catch (error) {
        console.error("Erreur chargement entreprises :", error);
      } finally {
        setLoading(false);
      }
    };

    loadEntreprises();
  }, []);

  // ================= NAVIGATION =================

  const navigateToCreateEntreprise = () => {
    navigate("/create-entreprise");
  };

  // ================= ACTIVATE / DEACTIVATE =================

  const handleToggleEntreprise = (entreprise) => {
    const newStatus = entreprise.active === 1 ? 0 : 1;

    confirmAlert({
      title: "Confirmation",
      message: `Voulez-vous ${
        newStatus === 0 ? "désactiver" : "activer"
      } cette entreprise ?`,
      buttons: [
        {
          label: "Oui",
          onClick: () => {
            updateEntreprise(entreprise._id, { active: newStatus }, () => {
              setEntreprises((prev) =>
                prev.map((e) =>
                  e._id === entreprise._id ? { ...e, active: newStatus } : e,
                ),
              );

              setMessage({
                type: "success",
                text: `Entreprise ${
                  newStatus === 0 ? "désactivée" : "activée"
                } avec succès.`,
              });

              setTimeout(() => setMessage(null), 2000);
            });
          },
        },
        { label: "Non" },
      ],
    });
  };

  // ================= DATE FORMAT =================

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("fr-FR");
  };

  // ================= FILTER ENTREPRISES =================

  const filteredEntreprises = entreprises
    .filter((entreprise) => {
      const fullText = `${entreprise.nom} ${entreprise.adresse || ""} ${
        entreprise.offre?.designation || ""
      }`.toLowerCase();

      return fullText.includes(searchTerm.toLowerCase());
    })
    .filter((entreprise) => {
      if (statutFilter === "all") return true;
      if (statutFilter === "active") return entreprise.active === 1;
      if (statutFilter === "inactive") return entreprise.active !== 1;
      return true;
    });

  // ================= PAGINATION =================

  const totalPages = Math.ceil(filteredEntreprises.length / entreprisesPerPage);
  const indexOfLast = currentPage * entreprisesPerPage;
  const indexOfFirst = indexOfLast - entreprisesPerPage;
  const currentEntreprises = filteredEntreprises.slice(
    indexOfFirst,
    indexOfLast,
  );

  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const goToPrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  // ================= RENDER =================

  return (
    <div className="container-fluid">
      {/* TITLE */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h2 className="h4 mb-0">Entreprises</h2>
      </div>

      {/* ALERT MESSAGE */}
      {message && (
        <div
          className={`alert ${
            message.type === "success" ? "alert-success" : "alert-danger"
          }`}
          role="alert"
        >
          {message.text}
        </div>
      )}

      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white">
          <h3 className="h6 mb-0">Liste des entreprises</h3>
        </div>

        <div className="card-body">
          {loading ? (
            <Loader />
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                {/* SEARCH */}
                <div className="position-relative" style={{ width: "300px" }}>
                  <FiSearch className="position-absolute top-50 start-0 translate-middle-y ms-2 text-secondary" />
                  <input
                    type="text"
                    className="form-control ps-4 rounded-pill"
                    placeholder="Rechercher entreprise..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ paddingLeft: "35px" }}
                  />
                </div>

                {/* FILTER */}
                <div className="d-flex align-items-center gap-2">
                  <FiFilter className="text-secondary" />
                  <select
                    className="form-select rounded-pill"
                    value={statutFilter}
                    onChange={(e) => setStatutFilter(e.target.value)}
                    style={{ width: "180px" }}
                  >
                    <option value="all">Tous les statuts</option>
                    <option value="active">Activées</option>
                    <option value="inactive">Désactivées</option>
                  </select>
                </div>
              </div>

              {/* TABLE */}
              <div className="table-responsive">
                <table className="table align-middle">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Nom</th>
                      <th>Adresse</th>
                      <th>Offre</th>
                      <th>Date début</th>
                      <th>Expiration</th>
                      <th>Abonnement</th>
                      <th>Ouverture</th>
                      <th>Fermeture</th>
                      <th>Tolérance pause</th>
                      <th>Statut</th>
                      <th>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {currentEntreprises.map((entreprise) => (
                      <tr key={entreprise._id}>
                        <td className="text-center">
                          <img
                            src={
                              entreprise.logo?.startsWith("data:")
                                ? entreprise.logo
                                : defaultImage
                            }
                            alt="Logo"
                            className="rounded-circle"
                            style={{
                              width: "40px",
                              height: "40px",
                              objectFit: "cover",
                              border: "1px solid #ccc",
                            }}
                          />
                        </td>

                        <td>{entreprise.nom}</td>
                        <td>{entreprise.adresse || "_"}</td>
                        <td>{entreprise.offre?.designation || "—"}</td>
                        <td>{formatDate(entreprise.dateDebut)}</td>
                        <td>{formatDate(entreprise.dateExpiration)}</td>

                        <td className="text-center">
                          {entreprise.statutAbonnement === 1 ? (
                            <span className="badge bg-success">Actif</span>
                          ) : (
                            <span className="badge bg-warning text-dark">
                              Inactif
                            </span>
                          )}
                        </td>

                        <td>{entreprise.heureOuverture || "—"}</td>
                        <td>{entreprise.heureFermeture || "—"}</td>
                        <td>{entreprise.tolerancePauseMinutes || "—"}</td>

                        <td className="text-center">
                          {entreprise.active === 1 ? (
                            <span className="badge bg-success">Activée</span>
                          ) : (
                            <span className="badge bg-secondary">
                              Désactivée
                            </span>
                          )}
                        </td>

                        <td>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() =>
                                navigate(`/edit-entreprise/${entreprise._id}`)
                              }
                            >
                              Modifier
                            </button>

                            <button
                              className={`btn btn-${
                                entreprise.active === 1 ? "danger" : "success"
                              } btn-sm`}
                              onClick={() => handleToggleEntreprise(entreprise)}
                            >
                              {entreprise.active === 1
                                ? "Désactiver"
                                : "Activer"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* PAGINATION */}
              <div className="d-flex justify-content-between align-items-center mt-3">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={goToPrevPage}
                  disabled={currentPage === 1}
                >
                  Précédent
                </button>

                <span className="small text-secondary">
                  Page {currentPage} sur {totalPages || 1}
                </span>

                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  Suivant
                </button>
              </div>

              {/* CREATE ENTREPRISE */}
              <div className="text-center mt-3">
                <button
                  className="btn btn-success btn-sm"
                  onClick={navigateToCreateEntreprise}
                >
                  Créer Entreprise
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default EntreprisesList;
